import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Play,
  Pause,
  Clock,
  Calendar,
  FileText,
  Share2,
  Check,
  ListOrdered,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import Seo from "@/components/Seo";
import TranscriptPanel from "@/components/TranscriptPanel";
import Participants from "@/components/Participants";
import { usePlayer } from "@/context/PlayerContext";
import {
  show,
  getEpisodeBySlug,
  getAdjacent,
  formatDate,
  formatDuration,
  formatClock,
} from "@/lib/podcast";
import NotFound from "@/pages/NotFound";

export default function EpisodeDetail() {
  const { slug } = useParams();
  const ep = getEpisodeBySlug(slug);
  const { playEpisode, toggle, seek, isActive, isPlaying, time } = usePlayer();
  const [copied, setCopied] = useState(false);

  if (!ep) return <NotFound />;

  const { newer, older } = getAdjacent(slug);
  const active = isActive(ep);

  const share = async () => {
    const url = `${show.link}/episodes/${ep.slug}`;
    try {
      if (navigator.share) await navigator.share({ title: ep.title, url });
      else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      /* cancelled */
    }
  };

  const jumpTo = (t) => {
    if (active) seek(t);
    else playEpisode(ep, t);
  };

  return (
    <div data-testid="episode-detail-page">
      <Seo
        title={ep.title}
        description={ep.excerpt}
        image={ep.image}
        path={`/episodes/${ep.slug}`}
        type="article"
      />

      {/* Hero */}
      <section className="hero-bg border-b border-[#0b1f4d]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <Link
            to="/episodes"
            data-testid="back-to-episodes"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#4d5a78] hover:text-[#9a6700] transition-colors mb-6"
          >
            <ArrowLeft size={16} /> All episodes
          </Link>

          <div className="flex flex-col sm:flex-row gap-8">
            <img
              src={ep.image}
              alt={ep.title}
              className="w-full sm:w-56 aspect-square rounded-lg object-cover ring-1 ring-[#0b1f4d]/10 shrink-0"
            />
            <div className="flex-1 min-w-0">
              {ep.episodeNumber != null && (
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9a6700]">
                  Episode {ep.episodeNumber}
                </span>
              )}
              <h1 className="font-display uppercase text-3xl sm:text-4xl font-semibold tracking-tight text-[#0b1f4d] leading-tight mt-1">
                {ep.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-mono text-[#4d5a78]">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={14} /> {formatDate(ep.pubDate)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={14} /> {formatDuration(ep.durationSeconds)}
                </span>
                {show.person && <span>with {show.person.name}</span>}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  data-testid="detail-play-button"
                  onClick={() => (active ? toggle() : playEpisode(ep))}
                  className="inline-flex items-center gap-2 rounded-sm bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] font-semibold uppercase tracking-wide px-6 py-3 transition-colors"
                >
                  {active && isPlaying ? (
                    <>
                      <Pause size={18} /> Pause
                    </>
                  ) : (
                    <>
                      <Play size={18} /> {active ? "Resume" : "Play episode"}
                    </>
                  )}
                </button>
                <button
                  data-testid="detail-share-button"
                  onClick={share}
                  className="inline-flex items-center gap-2 rounded-sm border border-[#0b1f4d]/20 hover:border-[#f5b61c] text-[#0b1f4d] font-semibold uppercase tracking-wide px-5 py-3 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check size={17} /> Copied
                    </>
                  ) : (
                    <>
                      <Share2 size={17} /> Share
                    </>
                  )}
                </button>
                {ep.transcriptUrl && (
                  <a
                    href={ep.transcriptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="detail-transcript-link"
                    className="inline-flex items-center gap-2 rounded-sm border border-[#0b1f4d]/20 hover:border-[#f5b61c] text-[#0b1f4d] font-semibold uppercase tracking-wide px-5 py-3 transition-colors"
                  >
                    <FileText size={17} /> Transcript
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid lg:grid-cols-3 gap-12">
        {/* Show notes */}
        <article className="lg:col-span-2 min-w-0">
          <h2 className="font-display uppercase text-2xl font-semibold text-[#0b1f4d] mb-5">
            Show notes
          </h2>
          <div
            data-testid="episode-show-notes"
            className="show-notes"
            dangerouslySetInnerHTML={{ __html: ep.descriptionHtml }}
          />
          <Participants persons={ep.persons} />
        </article>

        {/* Chapters */}
        <aside className="lg:col-span-1">
          {ep.chapters && ep.chapters.length > 0 && (
            <div
              data-testid="episode-chapters"
              className="lg:sticky lg:top-24 bg-white rounded-md border border-[#0b1f4d]/10 overflow-hidden"
            >
              <div className="flex items-center gap-2 px-5 py-4 border-b border-[#0b1f4d]/10 bg-[#f3ecdc] text-[#0b1f4d]">
                <ListOrdered size={16} className="text-[#9a6700]" />
                <h3 className="font-mono text-xs uppercase tracking-[0.2em]">
                  Chapters
                </h3>
              </div>
              <ol className="divide-y divide-[#0b1f4d]/10">
                {ep.chapters.map((c, i) => {
                  const next = ep.chapters[i + 1];
                  const isCurrent =
                    active &&
                    time >= c.startTime &&
                    (!next || time < next.startTime);
                  return (
                    <li key={i}>
                      <button
                        data-testid={`chapter-${i}`}
                        onClick={() => jumpTo(c.startTime)}
                        className={`w-full text-left flex items-start gap-3 px-5 py-3 transition-colors hover:bg-[#f5b61c]/10 ${
                          isCurrent ? "bg-[#f5b61c]/15" : ""
                        }`}
                      >
                        <span className="font-mono text-xs text-[#9a6700] pt-0.5 shrink-0 w-12">
                          {formatClock(c.startTime)}
                        </span>
                        <span
                          className={`text-sm leading-snug ${
                            isCurrent
                              ? "text-[#0b1f4d] font-semibold"
                              : "text-[#374566]"
                          }`}
                        >
                          {c.title}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </aside>
      </div>

      {/* Inline transcript */}
      <TranscriptPanel ep={ep} active={active} time={time} onJump={jumpTo} />

      {/* Prev / next */}
      <section className="border-t border-[#0b1f4d]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid sm:grid-cols-2 gap-4">
          {older ? (
            <Link
              to={`/episodes/${older.slug}`}
              data-testid="older-episode-link"
              className="group rounded-md border border-[#0b1f4d]/10 p-5 hover:border-[#f5b61c]/50 hover:bg-[#f3ecdc] transition-colors"
            >
              <span className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-[#66718d]">
                <ArrowLeft size={13} /> Older
              </span>
              <p className="mt-2 font-display uppercase font-semibold text-[#0b1f4d] group-hover:text-[#9a6700] transition-colors line-clamp-2">
                {older.title}
              </p>
            </Link>
          ) : (
            <div />
          )}
          {newer && (
            <Link
              to={`/episodes/${newer.slug}`}
              data-testid="newer-episode-link"
              className="group rounded-md border border-[#0b1f4d]/10 p-5 hover:border-[#f5b61c]/50 hover:bg-[#f3ecdc] transition-colors text-right"
            >
              <span className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-[#66718d] justify-end w-full">
                Newer <ArrowRight size={13} />
              </span>
              <p className="mt-2 font-display uppercase font-semibold text-[#0b1f4d] group-hover:text-[#9a6700] transition-colors line-clamp-2">
                {newer.title}
              </p>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
